import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { siteConfig } from '../config/site';
import { getLaunchReturnOrigin } from '../lib/oauthRelay';

/**
 * V1.5.M10 white-label branding — B-P1 theming engine.
 *
 * Resolves the brand for this pageview from the consumer backend
 * (GET /branding/resolve, shipped B-P0) and applies it:
 *   - palette: overrides the Tailwind v4 `@theme` CSS variables
 *     (--color-primary-25..900 etc.) on <html> via inline style. The compiled
 *     CSS already reads var() everywhere, so no component changes are needed
 *     for colors.
 *   - identity: document.title + favicon swap; logo/name/banner via the
 *     useBranding() hook (siteConfig stays the single 2Connect default).
 *   - localStorage bootstrap: the last resolved brand for THIS origin is
 *     applied synchronously on mount (no 2Connect flash on a client host),
 *     then revalidated in the background.
 *
 * Resolution inputs (server decides precedence: invite -> host -> user ->
 * default): ?invite= URL param or the pending-invite stash that AuthContext
 * already writes; window.location.host; the auth-token JWT when present
 * (sent as a Bearer header, verified best-effort server-side).
 *
 * Fail-safe: ANY error keeps/returns the 2Connect default. The backend
 * response uses the global { code, message, result } envelope — read .result.
 */
export interface ResolvedBranding {
  brand_name: string;
  logo_url: string | null;
  logo_dark_url: string | null;
  banner_url: string | null;
  favicon_url: string | null;
  palette: Record<string, string> | null;
  sponsors_line: string | null;
  hide_powered_by: boolean;
  is_default: boolean;
}

const DEFAULT_BRANDING: ResolvedBranding = {
  brand_name: siteConfig.name,
  logo_url: null,
  logo_dark_url: null,
  banner_url: null,
  favicon_url: null,
  palette: null,
  sponsors_line: null,
  hide_powered_by: true,
  is_default: true,
};

const STORAGE_KEY = 'branding';
const INITIAL_TITLE =
  typeof document !== 'undefined' ? document.title : siteConfig.name;

function readCache(): ResolvedBranding | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ResolvedBranding;
    return typeof parsed?.brand_name === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/** Apply (or clear) the brand on the live document. Idempotent. */
function applyToDocument(b: ResolvedBranding) {
  const root = document.documentElement;
  if (!b.is_default && b.palette) {
    for (const [key, value] of Object.entries(b.palette)) {
      // Only ever touch theme color vars — never arbitrary CSS properties.
      if (key.startsWith('--color-') && typeof value === 'string') {
        root.style.setProperty(key, value);
        // V1.5.M10 B-P2: the legacy fixed `purple-*` palette (a lavender AI/
        // agent accent compiled into @theme, used in ~40 components: the AI
        // conversation icon, inputs, buttons, gradients, etc.) does NOT follow
        // primary/secondary, so on a client host it would stay 2Connect purple.
        // Tailwind v4 compiles `bg-purple-100` -> var(--color-purple-100), so
        // mirroring each brand primary shade onto the matching purple shade
        // themes every purple-* usage at once. Default brand is untouched (this
        // branch only runs for a real client palette), so its lavender accents
        // are preserved.
        if (key.startsWith('--color-primary-')) {
          root.style.setProperty(
            key.replace('--color-primary-', '--color-purple-'),
            value,
          );
        }
      }
    }
  } else {
    // Default brand: remove any earlier overrides (primary, secondary, AND the
    // mirrored purple) so the compiled @theme defaults win again.
    for (const prop of Array.from(root.style)) {
      if (prop.startsWith('--color-')) root.style.removeProperty(prop);
    }
  }

  document.title = b.is_default ? INITIAL_TITLE : b.brand_name;

  if (b.favicon_url) {
    document
      .querySelectorAll<HTMLLinkElement>('link[rel*="icon"]')
      .forEach(link => {
        link.href = b.favicon_url as string;
      });
  }
}

async function resolveFromApi(): Promise<ResolvedBranding> {
  const base = import.meta.env.VITE_API_BACKEND_BASE_URL;
  const params = new URLSearchParams();
  const invite =
    new URLSearchParams(window.location.search).get('invite') ||
    localStorage.getItem('pending-invite');
  if (invite) params.set('invite', invite);
  // V1.5.M10 B-P2: on the canonical Google launch interstitial
  // (/oauth/launch?return=<client>), brand by the RETURN subdomain (the
  // client) rather than app.2connect.ai, so even the brief sign-in hop shows
  // the client's brand for non-invited visitors. Invite still wins server-side
  // (invite > host), so invited users are unaffected.
  const launchReturn = getLaunchReturnOrigin();
  params.set('host', launchReturn ? new URL(launchReturn).host : window.location.host);

  const headers: Record<string, string> = {};
  const token = localStorage.getItem('auth-token');
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${base}/branding/resolve?${params.toString()}`, {
    headers,
  });
  if (!res.ok) throw new Error(`branding resolve HTTP ${res.status}`);
  const body = (await res.json()) as { result?: ResolvedBranding };
  const branding = body?.result;
  if (!branding || typeof branding.brand_name !== 'string') {
    throw new Error('branding resolve: malformed response');
  }
  return branding;
}

interface BrandingContextValue {
  branding: ResolvedBranding;
  /** Brand display name (client brand or "2Connect Professional"). */
  name: string;
  /** Light/white logo, shown on dark/coloured surfaces (the sign-in banner). */
  logo: string;
  /** Dark/bold logo, shown on light/white surfaces (header, sidebar, mobile login). */
  logoDark: string;
  /** Favicon / small square mark (also used as the AI-chat avatar). */
  favicon: string;
  /** Optional brand banner image (login + dashboard hero). null = none. */
  bannerUrl: string | null;
  /** Sponsor names for the "Sponsored by …" line, or null. */
  sponsorsLine: string | null;
  isDefault: boolean;
  /** Show the "Powered by 2Connect" footer? */
  showPoweredBy: boolean;
  /** True once the authoritative /branding/resolve call has completed (success or
   *  fail). Consumers that must send the correct brand (e.g. the onboarding greeting's
   *  organizer_name) should wait for this so a first-load DEFAULT brand is not used
   *  prematurely (a user got "2Connect Professional" on a TokenizeThis link). */
  resolved: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: DEFAULT_BRANDING,
  name: siteConfig.name,
  logo: siteConfig.logo,
  logoDark: siteConfig.logoDark,
  favicon: siteConfig.favIcon,
  bannerUrl: null,
  sponsorsLine: null,
  isDefault: true,
  showPoweredBy: false,
  resolved: false,
});

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Bootstrap synchronously from this origin's cache: a return visitor on a
  // branded host paints branded on first frame, no 2Connect flash.
  const [branding, setBranding] = useState<ResolvedBranding>(() => {
    const cached = readCache();
    return cached ?? DEFAULT_BRANDING;
  });
  // QA fix (Jun-29): true once the authoritative resolve completes, so onboarding can
  // wait for the correct organizer brand before sending the greeting's organizer_name.
  const [resolved, setResolved] = useState(false);

  // Apply whatever we currently believe, then revalidate in the background.
  useEffect(() => {
    applyToDocument(branding);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    resolveFromApi()
      .then(fresh => {
        if (cancelled) return;
        setBranding(fresh);
        applyToDocument(fresh);
        try {
          // On the launch interstitial we resolved a DIFFERENT host's brand
          // (the return subdomain), so do NOT cache it under this origin
          // (app.2connect.ai) — that would briefly flash the client brand on a
          // later plain app.2connect.ai load. Apply for this view only.
          if (getLaunchReturnOrigin()) return;
          if (fresh.is_default) localStorage.removeItem(STORAGE_KEY);
          else localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
        } catch {
          /* storage full/blocked — branding still applied this session */
        }
      })
      .catch(() => {
        /* fail-safe: keep current (cached or default) branding */
      })
      .finally(() => {
        // Mark resolved on success OR fail so onboarding never blocks forever.
        if (!cancelled) setResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // QA fix (Jun-29): safety net. /branding/resolve has no fetch timeout, so if it
  // hangs (vs rejects) the .finally above never runs and onboarding (which now waits
  // for `resolved`) would stall. Force resolved after 4s so onboarding always proceeds
  // with whatever brand we have (cached or default).
  useEffect(() => {
    const t = setTimeout(() => setResolved(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const value = useMemo<BrandingContextValue>(
    () => ({
      branding,
      name: branding.is_default ? siteConfig.name : branding.brand_name,
      logo: branding.logo_url ?? siteConfig.logo,
      logoDark: branding.logo_dark_url ?? siteConfig.logoDark,
      favicon: branding.favicon_url ?? siteConfig.favIcon,
      bannerUrl: branding.banner_url,
      // Only client brands carry a sponsor line; the 2Connect default never does.
      sponsorsLine: branding.is_default ? null : branding.sponsors_line,
      isDefault: branding.is_default,
      // Default brand never shows the footer (it IS 2Connect); client brands
      // show it unless their row opts out.
      showPoweredBy: !branding.is_default && !branding.hide_powered_by,
      resolved,
    }),
    [branding, resolved],
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}

/** "Powered by 2Connect" footer line for white-labeled surfaces. */
export const PoweredByFooter: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { showPoweredBy } = useBranding();
  if (!showPoweredBy) return null;
  return (
    <p
      className={
        className ?? 'text-center text-xs text-gray-400 py-2 select-none'
      }
    >
      Powered by 2Connect
    </p>
  );
};

/**
 * "Sponsored by …" line for white-labeled surfaces (sign-in, onboarding,
 * dashboard). Renders only when the resolved client brand set a sponsor line;
 * the 2Connect default brand never shows it.
 */
export const SponsorLine: React.FC<{ className?: string }> = ({ className }) => {
  const { sponsorsLine } = useBranding();
  const text = sponsorsLine?.trim();
  if (!text) return null;
  return (
    <p className={className ?? 'text-center text-xs text-gray-500 mt-1 select-none'}>
      Sponsored by {text}
    </p>
  );
};
